import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, Mail, Clock, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

export default function ThankYouPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Google Tag Manager Script
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PXT9TWRF');`;
    document.head.appendChild(gtmScript);

    // Google Tag Manager noscript
    const gtmNoscript = document.createElement('noscript');
    gtmNoscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PXT9TWRF" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(gtmNoscript, document.body.firstChild);

    return () => {
      // Cleanup on unmount
      if (gtmScript.parentNode) gtmScript.parentNode.removeChild(gtmScript);
      if (gtmNoscript.parentNode) gtmNoscript.parentNode.removeChild(gtmNoscript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl"
      >
        <Card className="border-2 shadow-2xl" data-testid="thank-you-card">
          <CardContent className="p-8 md:p-12 text-center space-y-6">
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="flex justify-center"
            >
              <div className="rounded-full bg-green-100 p-6">
                <CheckCircle2 className="w-16 h-16 text-green-600" strokeWidth={2} />
              </div>
            </motion.div>

            {/* Main Message */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="thank-you-title">
                Thank You For Your Quote Request!
              </h1>
              <p className="text-lg text-slate-600 max-w-lg mx-auto">
                We&apos;ve received your enquiry and our team will review your project details shortly.
              </p>
            </div>

            {/* What Happens Next */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 space-y-4 text-left">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                What Happens Next?
              </h2>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Our team will review your project within <strong>24 hours</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>We&apos;ll contact you via phone or email to discuss your requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>If needed, we&apos;ll arrange a <strong>free on-site inspection</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>You&apos;ll receive a detailed quote with no hidden fees</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="border-t pt-6 space-y-4">
              <p className="text-slate-600 font-medium">
                Need immediate assistance?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="call-us-button"
                >
                  <a href="tel:+61478076070" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call Us: 0478 076 070
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  data-testid="email-us-button"
                >
                  <a href="mailto:sydneyconcretepolishers@gmail.com" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Us
                  </a>
                </Button>
              </div>
            </div>

            {/* Return Home */}
            <div className="pt-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900"
                data-testid="return-home-button"
              >
                <Home className="w-4 h-4 mr-2" />
                Return to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-slate-500 text-sm mt-6"
        >
          Sydney Concrete Polishers • Servicing Greater Sydney • ABN: Available on Request
        </motion.p>
      </motion.div>
    </div>
  );
}
